<template>
    <div v-if="user" v-resize="onResize" class="fill-height" ref="container">
        <v-tabs
            ref="tabs"
            active-class="white"
            background-color="grey lighten-3"
            hide-slider
            show-arrows
            :loading="loading"
        >
            <v-tab :to="{ name: 'userGeneralInfo', params: { user } }">
                General Information
            </v-tab>
            <v-tab :to="{ name: 'userDocuments', params: { user } }">
                Documents
            </v-tab>
            <v-tab
                v-if="readPermissions"
                :to="{ name: 'userPermissions', params: { user } }"
            >
                Permissions
            </v-tab>
            <v-tab
                v-if="readPermissions"
                :to="{ name: 'userlogs', params: { user, id } }"
            >
                Logs
            </v-tab>
        </v-tabs>
        <v-card flat>
            <v-card-title ref="title">
                <v-btn
                    fab
                    depressed
                    @click="show"
                    icon
                    v-if="$vuetify.breakpoint.smAndDown"
                >
                    <v-icon size="36">
                        mdi-chevron-left
                    </v-icon>
                </v-btn>
                <v-avatar size="46" class="mx-2">
                    <v-img
                        v-if="user.profilePicURL"
                        :src="user.profilePicURL"
                        :alt="user.name"
                    />
                    <v-icon size="48" v-else>mdi-account-circle</v-icon>
                </v-avatar>
                <span v-if="user">{{ user.name }}</span>
                <v-spacer />
                <v-switch
                    :input-value="enabledSwitch"
                    color="primary"
                    class="my-0 py-0 mr-4"
                    hide-details
                    inset
                    :loading="loading"
                    :disabled="!createUsersPermission"
                    @change="handleSwitchToggle"
                />
                <v-menu rounded offset-y>
                    <template v-slot:activator="{ attrs, on }">
                        <v-btn
                            fab
                            v-bind="attrs"
                            v-on="on"
                            small
                            color="grey lighten-3"
                            depressed
                            class="mx-1"
                        >
                            <v-icon>mdi-dots-horizontal</v-icon>
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item @click="openDialog">
                            Update User
                        </v-list-item>
                    </v-list>
                </v-menu>
                <v-dialog
                    v-if="user"
                    :retain-focus="false"
                    v-model="userDialog"
                    persistent
                    max-width="600px"
                >
                    <UserForm
                        v-if="userDialog"
                        title="User Information"
                        :createForm="false"
                        @closeDialog="closeDialog"
                        :cardDialog="false"
                        :user="this.userToUpdate"
                        :image="this.user.profilePicURL"
                        :originalUser="this.originalUser"
                        @replaceUser="this.replaceUser"
                        @removeUser="this.removeUser"
                    />
                </v-dialog>
                <v-dialog
                    v-model="disableDialog"
                    max-width="800px"
                    :retain-focus="false"
                    persistent
                >
                    <ReplaceAndDisableUser
                        v-if="disableDialog"
                        :user="user"
                        @userDisabled="onUserDisabled"
                        @close="onCancelDisable"
                    />
                </v-dialog>
            </v-card-title>

            <v-card-subtitle ref="subtitle">
                <v-divider />
            </v-card-subtitle>

            <v-card-text
                class="overflow-y-auto"
                :style="{ height: `${height}px` }"
            >
                <router-view />
            </v-card-text>
        </v-card>
        <!--ALERT-->
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
    </div>
</template>

<script>
import API from '@/services/api'
import UserForm from '@/components/Users/UserForm'
import { mapActions, mapMutations } from 'vuex'
import ReplaceAndDisableUser from '@/components/Users/ReplaceAndDisableUser.vue'

export default {
    name: 'User',
    components: {
        UserForm,
        ReplaceAndDisableUser,
    },
    props: {
        id: String,
        user: {},
    },
    data() {
        return {
            height: 0,
            userDialog: false,
            userToUpdate: null,
            originalUser: null,
            companyId: JSON.parse(localStorage.getItem('company')),
            idProject: null,
            currentUser: JSON.parse(localStorage.getItem('userId')),
            readPermissions: false,
            loading: false,
            createUsersPermission: false,
            disableDialog: false,
            enabledSwitch: true,
            previousEnabledState: true,
            previousSwitch: true,
            activateAlert: false,
            alertMessage: '',
        }
    },
    inject: {
        closeDetail: {
            from: 'closeDetail',
            default() {
                return null
            },
        },
        upsertUserInList: {
            from: 'upsertUserInList',
            default() {
                return null
            },
        },
        removeUserFromList: {
            from: 'removeUserFromList',
            default() {
                return null
            },
        },
    },
    async created() {
        const {
            data: { user },
        } = await API.getMyInfo()
        const readPermissions = user.permissions.find(
            x => x == 'readPermissions'
        )
        if (readPermissions) {
            this.readPermissions = true
        }
        this.createUsersPermission = user.permissions.includes('createUsers')
        await this.syncEnabledSwitch()
    },
    watch: {
        'user.id'() {
            this.syncEnabledSwitch()
        },
    },
    mounted() {
        try {
            this.onResize()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        readEnabled(enabled) {
            if (typeof enabled === 'boolean') return enabled
            if (enabled && typeof enabled === 'object') return !!enabled.enabled
            return this.previousSwitch
        },
        async syncEnabledSwitch() {
            const enabled = this.user && this.user.enabled
            if (
                typeof enabled === 'boolean' ||
                (enabled && typeof enabled === 'object')
            ) {
                this.enabledSwitch = this.readEnabled(enabled)
                return
            }
            if (!this.user?.id) return
            const {
                data: { user: authUser },
            } = await API.getOne(this.user.id)
            this.enabledSwitch = !!authUser.enabled
        },
        onUserDisabled() {
            this.user.enabled = false
            this.enabledSwitch = false
            this.disableDialog = false
        },
        onCancelDisable() {
            this.disableDialog = false
            this.user.enabled = this.previousEnabledState
            this.enabledSwitch = this.readEnabled(this.previousEnabledState)
        },
        async handleSwitchToggle(newValue) {
            this.previousEnabledState = this.user.enabled
            this.previousSwitch = this.enabledSwitch
            this.user.enabled = newValue
            this.enabledSwitch = newValue

            if (!newValue) {
                // Verificar si hay procesos asociados al usuario antes de mostrar el modal
                try {
                    this.loading = true
                    const processes = await API.userProcesses(this.user.id)

                    // Verificar si hay procesos con items que necesitan ser reemplazados
                    const hasItemsToReplace =
                        processes &&
                        processes.some(
                            process => process.items && process.items.length > 0
                        )

                    if (hasItemsToReplace) {
                        this.disableDialog = true
                    } else {
                        // Si no hay items que reemplazar, deshabilitar directamente
                        await this.setUserEnabled(false)
                        this.alertMessage = `User ${this.user.name} has been disabled successfully. The user had no pending processes.`
                        this.activateAlert = true
                        // Ocultar la alerta después de 5 segundos
                        setTimeout(() => {
                            this.activateAlert = false
                        }, 5000)
                    }
                } catch (error) {
                    this.user.enabled = this.previousEnabledState
                    this.enabledSwitch = this.readEnabled(
                        this.previousEnabledState
                    )
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message,
                    })
                } finally {
                    this.loading = false
                }
            } else {
                this.setUserEnabled(true)
            }
        },

        async setUserEnabled(value) {
            try {
                this.loading = true

                await API.disableUser({
                    userId: this.user.id,
                    enabled: value,
                })

                this.user.enabled = value
                this.enabledSwitch = value
            } catch (error) {
                this.user.enabled = this.previousEnabledState
                this.enabledSwitch = this.readEnabled(this.previousEnabledState)
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        onResize() {
            const container = this.$refs.container
            const tabs = this.$refs.tabs
            const title = this.$refs.title
            const subtitle = this.$refs.subtitle

            if (container && tabs?.$el && title && subtitle) {
                const containerHeight = container.clientHeight
                const tabsHeight = tabs.$el.clientHeight
                const titleHeight = title.clientHeight
                const subtitleHeight = subtitle.clientHeight

                this.height =
                    containerHeight -
                    tabsHeight -
                    titleHeight -
                    subtitleHeight -
                    40
            }
        },

        show() {
            if (this.$vuetify.breakpoint.smAndDown) {
                this.$emit('show')
            }
            this.closeDetail(false)
        },
        ...mapActions(['replaceUserAction', 'removeUserAction']),
        openDialog: function() {
            const userToUpdate = this.user
            this.userToUpdate = JSON.parse(JSON.stringify(userToUpdate))
            this.originalUser = JSON.parse(JSON.stringify(userToUpdate))
            this.userDialog = true
        },
        closeDialog: function() {
            this.userDialog = false
        },
        replaceUser: function(id, user) {
            this.replaceUserAction({ id, user })
            Object.assign(this.user, user)
            if (this.upsertUserInList) {
                this.upsertUserInList(user)
            }
            this.$router.currentRoute.params.user = this.user
        },
        removeUser: function(id) {
            this.removeUserAction({ id })
            if (this.removeUserFromList) {
                this.removeUserFromList(id)
            }
            this.$router.replace({ name: 'noUser' })
        },
    },
}
</script>
